import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { TrackableItem, TrackableList } from 'types/trackables';

import { TrackableItemSelect } from 'trackables/components/TrackableItemSelect/TrackableItemSelect';
import { useTrackableLists, useTrackableNames } from 'trackables/hooks';
import { useCurrentNode } from 'deployments/hooks/useCurrentNode';

import {
  formatCommaSeparatedValues,
  isValidUUID,
  parseCommaSeparatedValues,
} from './utils';
import { ReteInputProps } from 'pipelines/services/rete/controls/input';

function createPlaceholderItem(
  id: string,
  trackableListId?: string
): TrackableItem {
  return {
    id,
    name: id,
    description: null,
    labels: [],
    attributes: {},
    trackable_list_id: trackableListId ?? '',
    application_id: '',
    created_at: '',
    updated_at: '',
  };
}

function createPlaceholderList(id: string): TrackableList {
  return {
    id,
    name: id,
    description: null,
    object_type: 'custom',
    application_id: '',
    created_at: '',
    updated_at: '',
  };
}

export type TrackableItemPropertyInputProps = ReteInputProps & {
  trackableListField?: string;
};

const TRACKABLE_LIST_FIELD_FALLBACKS = [
  'trackables_list_ids',
  'trackables_list_id',
] as const;

function pickFirstStringValue(values: unknown): string | null {
  const entries = Array.isArray(values) ? values : [values];

  for (const entry of entries) {
    if (typeof entry !== 'string') {
      continue;
    }

    const trimmed = entry.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }

  return null;
}

export function TrackableItemPropertyInput({
  value,
  onChange,
  placeholder,
  trackableListField,
}: TrackableItemPropertyInputProps) {
  const { control } = useFormContext();
  const { nodeID, node } = useCurrentNode();

  const parsedItemIds = React.useMemo(
    () => parseCommaSeparatedValues(value),
    [value]
  );

  const selectedItemIds = React.useMemo(
    () => parsedItemIds.filter(isValidUUID),
    [parsedItemIds]
  );

  React.useEffect(() => {
    if (!parsedItemIds.length) {
      return;
    }

    if (parsedItemIds.length !== selectedItemIds.length) {
      onChange?.(formatCommaSeparatedValues(selectedItemIds));
    }
  }, [parsedItemIds, selectedItemIds, onChange]);

  const trackableListFields = React.useMemo(() => {
    if (
      typeof trackableListField === 'string' &&
      trackableListField.length > 0
    ) {
      return [trackableListField];
    }

    return Array.from(TRACKABLE_LIST_FIELD_FALLBACKS);
  }, [trackableListField]);

  const watchFieldNames = React.useMemo(() => {
    const names: string[] = [];

    if (typeof nodeID === 'string' && nodeID.length > 0) {
      for (const field of trackableListFields) {
        if (!field.includes('.')) {
          names.push(`${nodeID}.${field}`);
        }
      }
    }

    names.push(...trackableListFields);
    return Array.from(new Set(names));
  }, [nodeID, trackableListFields]);

  const watchedListValues = useWatch({
    control,
    name: watchFieldNames,
  });

  const fallbackListValue = React.useMemo(() => {
    if (!node) {
      return null;
    }

    for (const field of trackableListFields) {
      if (field.includes('.')) {
        continue;
      }

      const value = node.data?.[field];
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          return trimmed;
        }
      }
    }

    return null;
  }, [node, trackableListFields]);

  const watchedListValue = React.useMemo<string | null>(() => {
    const watchedValue = pickFirstStringValue(watchedListValues);
    return watchedValue ?? fallbackListValue;
  }, [watchedListValues, fallbackListValue]);

  const parsedListIds = React.useMemo(
    () => parseCommaSeparatedValues(watchedListValue),
    [watchedListValue]
  );
  const selectedListIds = React.useMemo(
    () => parsedListIds.filter(isValidUUID),
    [parsedListIds]
  );

  const trackableNamesQuery = useTrackableNames(selectedItemIds);

  const {
    data: selectedListsData,
    isFetching: isFetchingSelectedLists,
    isInitialLoading: isLoadingSelectedLists,
  } = useTrackableLists({
    queryKey: ['trackable-lists-for-items-property', selectedListIds],
    params: selectedListIds.length
      ? {
          trackable_list_ids: selectedListIds,
          limit: Math.max(selectedListIds.length, 50),
        }
      : undefined,
    enabled: selectedListIds.length > 0,
    keepPreviousData: true,
  });

  const fallbackListLookup = React.useMemo(() => {
    const lists = trackableNamesQuery.data?.lists;
    if (!lists) {
      return new Map<string, TrackableList>();
    }

    return new Map(
      Object.values(lists).map((list) => [list.id, list] as const)
    );
  }, [trackableNamesQuery.data]);

  const availableLists = React.useMemo(() => {
    if (!selectedListIds.length) {
      return [];
    }

    const lookup = new Map(
      selectedListsData?.data?.map((list) => [list.id, list] as const) ?? []
    );

    return selectedListIds.map(
      (id) =>
        lookup.get(id) ??
        fallbackListLookup.get(id) ??
        createPlaceholderList(id)
    );
  }, [selectedListIds, selectedListsData, fallbackListLookup]);

  const selectedItems = React.useMemo(() => {
    if (!selectedItemIds.length) {
      return [];
    }

    return selectedItemIds.map((id) => {
      const item = trackableNamesQuery.data?.items[id];
      if (item) {
        return item;
      }

      return createPlaceholderItem(id);
    });
  }, [selectedItemIds, trackableNamesQuery.data]);

  React.useEffect(() => {
    if (!selectedItemIds.length) {
      return;
    }

    if (!selectedListIds.length) {
      onChange?.(null);
      return;
    }

    const lookup = trackableNamesQuery.data?.items;
    if (!lookup) {
      return;
    }

    const invalidIds = selectedItemIds.filter((id) => {
      const item = lookup[id];
      return Boolean(item) && !selectedListIds.includes(item.trackable_list_id);
    });

    if (invalidIds.length) {
      const nextIds = selectedItemIds.filter((id) => !invalidIds.includes(id));
      onChange?.(formatCommaSeparatedValues(nextIds));
    }
  }, [selectedItemIds, selectedListIds, trackableNamesQuery.data, onChange]);

  const handleSelectionChange = React.useCallback(
    (items: readonly TrackableItem[] | TrackableItem | null) => {
      const selections: readonly TrackableItem[] = Array.isArray(items)
        ? items
        : items
          ? [items]
          : [];

      const ids = selections
        .map((item) => item.id?.trim())
        .filter((id): id is string => Boolean(id));

      onChange?.(formatCommaSeparatedValues(ids));
    },
    [onChange]
  );

  const isDisabled = selectedListIds.length === 0;
  const isLoadingItems =
    trackableNamesQuery.isFetching && !trackableNamesQuery.data;

  let resolvedPlaceholder = 'Select watchlist items';
  if (isDisabled) {
    resolvedPlaceholder = 'Select watchlists first';
  }
  if (typeof placeholder === 'string' && placeholder.length > 0) {
    resolvedPlaceholder = placeholder;
  }

  return (
    <TrackableItemSelect<true>
      isMulti
      isClearable
      value={selectedItems}
      trackableLists={availableLists}
      onChange={handleSelectionChange}
      placeholder={resolvedPlaceholder}
      isDisabled={isDisabled}
      isLoading={
        isLoadingItems ||
        isLoadingSelectedLists ||
        (isFetchingSelectedLists && !selectedListsData)
      }
      styles={{
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
    />
  );
}
